import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Key, Smartphone, Monitor, Clock, AlertTriangle,
  CheckCircle, XCircle, RefreshCw, LogOut, Lock, Eye, EyeOff,
} from 'lucide-react';
import api from '../api';

/* ── helpers ────────────────────────────────────────────────────────────────── */
const fmtDate = (d) => d ? new Date(d).toLocaleString("en-IN", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "-";

const calculateStrength = (pw) => {
  let s = 0;
  if (pw.length >= 6) s += 25;
  if (/[A-Z]/.test(pw)) s += 25;
  if (/[0-9]/.test(pw)) s += 25;
  if (/[^A-Za-z0-9]/.test(pw)) s += 25;
  return s;
};

const strengthLabel = (s) => s < 50 ? "Weak" : s < 75 ? "Good" : "Strong";
const strengthColor = (s) => s < 50 ? "#ef4444" : s < 75 ? "#f59e0b" : "#10b981";

/* ── Main Component ─────────────────────────────────────────────────────────── */
export default function MySecurity() {
  // Security summary
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [showPw, setShowPw] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);

  // Login history
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // 2FA
  const [twoFaLoading, setTwoFaLoading] = useState(false);

  /* ── Loaders ─────────────────────────────────────────────────────────────── */
  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await api.get("/auth/security-summary");
      setSummary(res.data);
    } catch { setSummary(null); }
    setSummaryLoading(false);
  }, []);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await api.get("/auth/my-sessions");
      setSessions(res.data?.sessions || []);
    } catch { setSessions([]); }
    setSessionsLoading(false);
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/auth/login-history");
      setHistory(res.data?.history || []);
    } catch { setHistory([]); }
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    loadSummary();
    loadSessions();
    loadHistory();
  }, [loadSummary, loadSessions, loadHistory]);

  /* ── Actions ─────────────────────────────────────────────────────────────── */
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(""); setPwSuccess("");
    if (newPassword !== confirmPassword) { setPwError("Passwords do not match."); return; }
    if (newPassword.length < 6) { setPwError("Password must be at least 6 characters."); return; }
    setPwLoading(true);
    try {
      await api.post("/change-password", { new_password: newPassword, confirm_password: confirmPassword });
      setPwSuccess("Password changed successfully!");
      setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      setPwError(err.response?.data?.error || "Failed to change password.");
    }
    setPwLoading(false);
  };

  const handleToggle2FA = async () => {
    setTwoFaLoading(true);
    try {
      const res = await api.post("/auth/toggle-2fa", { enable: !summary?.two_factor_enabled });
      setSummary(prev => ({ ...prev, two_factor_enabled: res.data.two_factor_enabled }));
    } catch { /* ignore */ }
    setTwoFaLoading(false);
  };

  const handleRevokeSession = async (id) => {
    setRevokingId(id);
    try {
      await api.post(`/auth/revoke-session/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      setSummary(prev => prev ? { ...prev, active_session_count: Math.max(0, (prev.active_session_count || 1) - 1) } : prev);
    } catch { /* ignore */ }
    setRevokingId(null);
  };

  const strength = calculateStrength(newPassword);
  const requirements = [
    { text: "Minimum 6 characters", met: newPassword.length >= 6 },
    { text: "Uppercase letter", met: /[A-Z]/.test(newPassword) },
    { text: "At least one number", met: /[0-9]/.test(newPassword) },
    { text: "Special character", met: /[^A-Za-z0-9]/.test(newPassword) },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", fontFamily: "'Inter', sans-serif" }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: "#1e293b", margin: "0 0 4px" }}>My Security</h2>
        <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>Manage your account security settings</p>
      </div>

      {/* Security Score Card */}
      <div style={{
        background: "linear-gradient(135deg, #1e1b4b, #312e81)", borderRadius: 16,
        padding: "24px 28px", color: "#fff", marginBottom: 24,
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "center",
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <Shield size={22} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>Security Score</span>
          </div>
          {summaryLoading ? (
            <div style={{ fontSize: 13, opacity: .7 }}>Loading...</div>
          ) : (
            <>
              <div style={{ fontSize: 48, fontWeight: 800, lineHeight: 1 }}>
                {summary?.security_score || 0}<span style={{ fontSize: 18, opacity: .6 }}>/100</span>
              </div>
              <div style={{
                height: 8, borderRadius: 4, background: "rgba(255,255,255,.15)", marginTop: 12, overflow: "hidden",
              }}>
                <div style={{
                  width: `${summary?.security_score || 0}%`, height: "100%", borderRadius: 4,
                  background: (summary?.security_score || 0) >= 75 ? "#10b981" : (summary?.security_score || 0) >= 50 ? "#f59e0b" : "#ef4444",
                  transition: "width .5s ease",
                }} />
              </div>
            </>
          )}
        </div>
        <div>
          {!summaryLoading && summary?.checklist?.map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "6px 0",
              fontSize: 13, opacity: item.met ? 1 : .6,
            }}>
              {item.met
                ? <CheckCircle size={16} color="#10b981" />
                : <XCircle size={16} color="#f87171" />}
              <span>{item.label}</span>
            </div>
          ))}
          {!summaryLoading && summary && (
            <div style={{ marginTop: 12, fontSize: 12, opacity: .6 }}>
              Last login: {fmtDate(summary.last_login_at)} · Active sessions: {summary.active_session_count || 0}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Left Column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Password Management */}
          <Card
            icon={<Key size={16} color="#6366f1" />}
            title="Change Password"
          >
            {pwError && <AlertBox type="error">{pwError}</AlertBox>}
            {pwSuccess && <AlertBox type="success">{pwSuccess}</AlertBox>}
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>New Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPw ? "text" : "password"}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    style={{
                      position: "absolute", right: 10, top: 10, background: "none",
                      border: "none", cursor: "pointer", color: "#94a3b8",
                    }}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {newPassword && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{
                      height: 4, borderRadius: 2, background: "#e2e8f0", overflow: "hidden",
                    }}>
                      <div style={{
                        width: `${strength}%`, height: "100%", borderRadius: 2,
                        background: strengthColor(strength), transition: "width .3s",
                      }} />
                    </div>
                    <div style={{ fontSize: 11, color: strengthColor(strength), fontWeight: 600, marginTop: 3 }}>
                      {strengthLabel(strength)}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  style={inputStyle}
                />
              </div>
              {/* Requirements */}
              <div style={{
                background: "#f8fafc", borderRadius: 10, padding: 12, marginBottom: 14,
                border: "1px solid #e2e8f0",
              }}>
                {requirements.map((r, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 12,
                  }}>
                    {r.met
                      ? <CheckCircle size={14} color="#10b981" />
                      : <XCircle size={14} color="#cbd5e1" />}
                    <span style={{ color: r.met ? "#1e293b" : "#94a3b8" }}>{r.text}</span>
                  </div>
                ))}
              </div>
              <button
                type="submit"
                disabled={pwLoading}
                style={{
                  ...btnPrimary,
                  opacity: pwLoading ? .6 : 1,
                }}
              >
                <Lock size={14} /> {pwLoading ? "Updating..." : "Change Password"}
              </button>
            </form>
          </Card>

          {/* Two-Factor Authentication */}
          <Card
            icon={<Smartphone size={16} color="#6366f1" />}
            title="Two-Factor Authentication"
          >
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 16px", borderRadius: 10,
              background: summary?.two_factor_enabled ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${summary?.two_factor_enabled ? "#86efac" : "#fca5a5"}`,
            }}>
              <div>
                <div style={{
                  fontWeight: 700, fontSize: 14,
                  color: summary?.two_factor_enabled ? "#166534" : "#991b1b",
                }}>
                  {summary?.two_factor_enabled ? "Enabled" : "Disabled"}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {summary?.two_factor_enabled
                    ? "A verification code is required when signing in."
                    : "Enable 2FA for enhanced account security."}
                </div>
              </div>
              <button
                onClick={handleToggle2FA}
                disabled={twoFaLoading || summaryLoading}
                style={{
                  padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                  border: "none", cursor: "pointer",
                  background: summary?.two_factor_enabled ? "#fef2f2" : "#f0fdf4",
                  color: summary?.two_factor_enabled ? "#dc2626" : "#16a34a",
                }}
              >
                {twoFaLoading ? "..." : summary?.two_factor_enabled ? "Disable" : "Enable"}
              </button>
            </div>
          </Card>
        </div>

        {/* Right Column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Active Sessions */}
          <Card
            icon={<Monitor size={16} color="#6366f1" />}
            title={`Active Sessions (${sessions.length})`}
            action={
              <button onClick={loadSessions} style={refreshBtnStyle}>
                <RefreshCw size={13} />
              </button>
            }
          >
            {sessionsLoading ? (
              <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Loading...</div>
            ) : sessions.length === 0 ? (
              <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No active sessions found.</div>
            ) : (
              <div style={{ maxHeight: 240, overflowY: "auto" }}>
                {sessions.map(s => (
                  <div key={s.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 0", borderBottom: "1px solid #f1f5f9", fontSize: 13,
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, color: "#1e293b" }}>
                        {(s.device_info || "Unknown Device").substring(0, 50)}
                      </div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>
                        Since {fmtDate(s.issued_at)}
                        {s.last_used_at && <> · Last used {fmtDate(s.last_used_at)}</>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevokeSession(s.id)}
                      disabled={revokingId === s.id}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                        border: "1px solid #fca5a5", background: "#fef2f2", color: "#dc2626",
                        cursor: "pointer",
                      }}
                    >
                      <LogOut size={11} /> {revokingId === s.id ? "..." : "Revoke"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Login History */}
          <Card
            icon={<Clock size={16} color="#6366f1" />}
            title="Recent Login Activity"
            action={
              <button onClick={loadHistory} style={refreshBtnStyle}>
                <RefreshCw size={13} />
              </button>
            }
          >
            {historyLoading ? (
              <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Loading...</div>
            ) : history.length === 0 ? (
              <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No login history available.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                      <th style={thStyle}>Time</th>
                      <th style={thStyle}>IP Address</th>
                      <th style={thStyle}>Device</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={tdStyle}>{fmtDate(h.timestamp)}</td>
                        <td style={tdStyle}><code style={{ fontSize: 11, color: "#64748b" }}>{h.ip_address}</code></td>
                        <td style={{ ...tdStyle, maxWidth: 150 }}>
                          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {h.device || "N/A"}
                          </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {h.success ? (
                            <span style={{
                              display: "inline-block", padding: "2px 10px", borderRadius: 10,
                              fontSize: 10, fontWeight: 700, background: "#d1fae5", color: "#065f46",
                            }}>Success</span>
                          ) : (
                            <span style={{
                              display: "inline-block", padding: "2px 10px", borderRadius: 10,
                              fontSize: 10, fontWeight: 700, background: "#fee2e2", color: "#991b1b",
                            }}>Failed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!historyLoading && summary?.failed_login_count > 0 && (
              <div style={{
                marginTop: 12, padding: "8px 12px", borderRadius: 8,
                background: "#fef3c7", border: "1px solid #fde68a",
                display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#92400e",
              }}>
                <AlertTriangle size={14} />
                {summary.failed_login_count} failed login attempt{summary.failed_login_count !== 1 ? "s" : ""} recorded.
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ── Reusable Card ──────────────────────────────────────────────────────────── */
function Card({ icon, title, action, children }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
      boxShadow: "0 2px 8px rgba(15,23,42,.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 20px", borderBottom: "1px solid #f1f5f9",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
          {icon} {title}
        </div>
        {action}
      </div>
      <div style={{ padding: "16px 20px" }}>
        {children}
      </div>
    </div>
  );
}

function AlertBox({ type, children }) {
  const isErr = type === "error";
  return (
    <div style={{
      padding: "10px 14px", borderRadius: 10, fontSize: 13, marginBottom: 12,
      background: isErr ? "#fef2f2" : "#f0fdf4",
      border: `1px solid ${isErr ? "#fca5a5" : "#86efac"}`,
      color: isErr ? "#dc2626" : "#15803d",
    }}>
      {children}
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────────────────────── */
const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5,
};
const inputStyle = {
  width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10,
  fontSize: 14, outline: "none", boxSizing: "border-box", color: "#1e293b", background: "#fff",
};
const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700,
  border: "none", cursor: "pointer",
  background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff",
};
const refreshBtnStyle = {
  background: "none", border: "1px solid #e2e8f0", borderRadius: 6,
  padding: 5, cursor: "pointer", color: "#64748b", display: "flex",
};
const thStyle = {
  padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700,
  color: "#64748b", textTransform: "uppercase", letterSpacing: ".4px",
};
const tdStyle = {
  padding: "8px 10px", color: "#1e293b", verticalAlign: "middle",
};
